import CONFIG from "@APP/config";
import {
  RequestPagination,
  ResponsePagination,
  OrganisationSearchTerm,
  OrganisationData,
  SubscriptionPlan,
  SubscriptionDetails,
  GracePeriod,
  OrganizationDetail,
  OrganizationUsersResponse,
} from "@APP/types";

import request from "../request";

export const getSearchedOrganisations = async (
  searchTerms: OrganisationSearchTerm,
  pagination: RequestPagination = { page: 0, entries: 25 },
  queryParams?: string,
) => {
  const { page = 0, entries = 25 } = pagination;
  const response = await request<{
    data: OrganisationData[];
    links: ResponsePagination;
    meta: { totalItems: number };
  }>({
    method: "GET",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/user-mgmt/admin/organisations/search?${queryParams}`,
    params: {
      page: `${page * entries};;;${entries}`,
      ...searchTerms,
    },
  });

  return response.data;
};

export const changeOrganisationBlockStatus = async (orgId: string, blocked: boolean) => {
  const response = await request({
    method: blocked ? "DELETE" : "POST",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/user-mgmt/admin/organisation/blocked`,
    data: { orgId: orgId },
  });

  return response;
};

export const getOrganizationById = async (orgId: string) => {
  const response = await request<OrganisationData>({
    method: "GET",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/user-mgmt/admin/organisation/${orgId}`,
  });

  return response.data;
};

export const changeOrganisationSubscriptionStatus = async (
  shouldActivateSubscription: boolean,
  orgId: string,
  id: string,
) => {
  const response = await request({
    method: shouldActivateSubscription ? "POST" : "DELETE",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/admin/subscriptions/${orgId}/${id}`,
  });

  return response;
};

export const getSubscriptionsBillingPlans = async (id: string) => {
  const response = await request<{ data: SubscriptionPlan[] }>({
    method: "GET",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/admin/subscriptions/plans/${id}`,
  });

  return response.data.data;
};

export const getSubscriptionsByOrgId = async (id: string) => {
  const response = await request<SubscriptionDetails>({
    method: "GET",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/admin/subscriptions/${id}`,
  });

  return response.data;
};

export const getGracePeriod = async (orgId: string, planId: string) => {
  const response = await request({
    method: "GET",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/admin/bills/grace-periods/plans/${planId}/organisations/${orgId}`,
  });

  return response.data;
};

export const setGracePeriod = async (data: GracePeriod) => {
  const response = await request({
    method: "POST",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/admin/bills/grace-periods`,
    data,
  });

  return response;
};

export const deleteGracePeriod = async (gracePeriodId: string, orgId: string) => {
  const response = await request({
    method: "DELETE",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/admin/bills/grace-periods/${gracePeriodId}/organisations/${orgId}`,
  });

  return response;
};

export const getOrganizationUsers = async (
  orgId: string,
  pagination: RequestPagination = { page: 0, entries: 25 },
): Promise<OrganizationUsersResponse> => {
  const { page = 0, entries = 25 } = pagination;
  const response = await request<OrganizationUsersResponse>({
    method: "GET",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/user-mgmt/organisations/${orgId}/users`,
    params: {
      page: `${page * entries};;;${entries}`,
    },
  });

  return response.data;
};

export const updateOrganization = async (orgId: string, data: any): Promise<OrganizationDetail> => {
  const response = await request<OrganizationDetail>({
    method: "PATCH",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/user-mgmt/organisations/${orgId}`,
    data,
  });

  return response.data;
};

export const activateSubscription = async (orgId: string) => {
  const response = await request({
    method: "PATCH",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/admin/subscriptions/${orgId}/status/active`,
  });

  return response;
};

export const getOrgBills = async (
  orgId: string,
  pagination: RequestPagination = { page: 0, entries: 25 },
) => {
  const { page = 0, entries = 25 } = pagination;

  const response = await request({
    method: "GET",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/admin/bills/search`,
    params: {
      page: `${page * entries};;;${entries}`,
      orgId,
    },
  });

  return response.data;
};

export const getReminders = async (
  orgId: string,
  pagination: RequestPagination = { page: 0, entries: 25 },
) => {
  const { page = 0, entries = 25 } = pagination;

  const response = await request({
    method: "GET",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/admin/bills/reminders/search`,
    params: {
      page: `${page * entries};;;${entries}`,
      orgId,
    },
  });

  return response.data;
};

export const searchOrganizationById = async (orgId: string): Promise<OrganizationDetail> => {
  const response = await request<OrganizationDetail>({
    method: "GET",
    url: `${CONFIG.API.ROOT_URL}/${CONFIG.API.VERSION}/user-mgmt/organisations/${orgId}`,
  });

  return response.data;
};
